import { useState } from 'react'

import type { FeatureExplanation } from '@/types'

/**
 * Why this match scored what it did.
 *
 * Contributions are signed - the interaction penalty subtracts - so this is a
 * diverging encoding: two hues that read as opposite around a neutral zero
 * baseline, never a rainbow and never a hue at the midpoint.
 */
export function Contributions({ features }: { features: FeatureExplanation[] }) {
  const [hover, setHover] = useState<string | null>(null)
  const [showTable, setShowTable] = useState(false)

  if (!features.length) {
    return (
      <p className="px-4 py-8 text-center text-sm text-ink-muted">
        No feature breakdown was recorded for this match.
      </p>
    )
  }

  const scale = Math.max(...features.map((f) => Math.abs(f.contribution)), 1)

  return (
    <div className="px-4 pb-4 pt-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <Legend />
        <button
          type="button"
          className="btn-ghost px-2 py-1 text-xs"
          onClick={() => setShowTable((value) => !value)}
          aria-pressed={showTable}
        >
          {showTable ? 'Show chart' : 'Show table'}
        </button>
      </div>

      {showTable ? (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th scope="col" className="pb-2 text-left text-xs font-medium uppercase tracking-wide text-ink-faint">
                Signal
              </th>
              <th scope="col" className="pb-2 text-right text-xs font-medium uppercase tracking-wide text-ink-faint">
                Value
              </th>
              <th scope="col" className="pb-2 text-right text-xs font-medium uppercase tracking-wide text-ink-faint">
                Weight
              </th>
              <th scope="col" className="pb-2 text-right text-xs font-medium uppercase tracking-wide text-ink-faint">
                Points
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature) => (
              <tr key={feature.name} className="border-t border-line">
                <td className="py-2 text-ink">{feature.label}</td>
                <td className="py-2 text-right tabular text-ink-muted">
                  {feature.value.toFixed(2)}
                </td>
                <td className="py-2 text-right tabular text-ink-muted">
                  {feature.weight.toFixed(2)}
                </td>
                <td className="py-2 text-right tabular text-ink">
                  {feature.contribution > 0 ? '+' : ''}
                  {feature.contribution.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="space-y-2.5">
          {features.map((feature) => {
            const negative = feature.contribution < 0
            const magnitude = (Math.abs(feature.contribution) / scale) * 50
            const dim = hover !== null && hover !== feature.name

            return (
              <li
                key={feature.name}
                onMouseEnter={() => setHover(feature.name)}
                onMouseLeave={() => setHover(null)}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-xs text-ink" title={feature.description}>
                    {feature.label}
                  </span>
                  <span className="shrink-0 text-xs font-medium tabular text-ink">
                    {feature.contribution > 0 ? '+' : ''}
                    {feature.contribution.toFixed(1)}
                  </span>
                </div>

                <div className="relative mt-1 h-2.5">
                  {/* Neutral zero baseline. */}
                  <div
                    className="absolute inset-y-0 left-1/2 w-px"
                    style={{ background: 'var(--axis)' }}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute inset-y-0 transition-all duration-300"
                    style={{
                      left: negative ? `${50 - magnitude}%` : '50%',
                      width: `${Math.max(magnitude, 0.4)}%`,
                      background: negative ? 'var(--diverge-neg)' : 'var(--diverge-pos)',
                      borderRadius: negative ? '4px 0 0 4px' : '0 4px 4px 0',
                      opacity: dim ? 0.45 : 1,
                    }}
                  />
                </div>

                {hover === feature.name && feature.description && (
                  <p className="mt-1 text-xs text-ink-faint">{feature.description}</p>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-xs text-ink-muted">
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ background: 'var(--diverge-pos)' }}
        />
        Adds to the score
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="inline-block h-2.5 w-2.5 rounded-sm"
          style={{ background: 'var(--diverge-neg)' }}
        />
        Subtracts
      </span>
    </div>
  )
}

/** A score meter. The fill is the accent; the track is a lighter step of it. */
export function ScoreMeter({ score, size = 'md' }: { score: number; size?: 'sm' | 'md' }) {
  const height = size === 'sm' ? 'h-1.5' : 'h-2'
  return (
    <div className="flex items-center gap-2">
      <div
        className={`${height} w-full min-w-[3rem] overflow-hidden rounded-full`}
        style={{ background: 'var(--wash)' }}
        role="meter"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Match score ${score} out of 100`}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(score, 2)}%`, background: 'var(--series-1)' }}
        />
      </div>
      <span className="shrink-0 text-xs font-semibold tabular text-ink">{score}</span>
    </div>
  )
}
